// owner-map: OP-07a.spa
// Every network call for ONE forecast's shares (GET/PUT /api/forecasts/:id/shares,
// Tasks 3.3/3.2) lives here, so ShareDialog.jsx stays pure-props and App.jsx grows
// by a hook CALL rather than a state cluster (hooks/ convention — see
// useCopyLinks.js, the closest existing analogue, for the same shape).
//
// SAME BOOBY-TRAPS AS useCopyLinks.js, PORTED VERBATIM:
//   - AbortController armed BEFORE the await (§7) — a controller created after
//     it is unreachable by the cleanup meant to cancel it.
//   - A monotonic `seqRef` guards against a SUPERSEDED read overwriting a newer
//     one (switching forecasts, or a save's follow-up refresh outracing a
//     stale in-flight read).
//   - `aliveRef` in its OWN effect with an empty dep array, so its cleanup runs
//     on UNMOUNT ONLY — the read effect's cleanup also fires on every
//     `forecastId` change, which is a different question.
//
// SAVE NEVER TRUSTS ITS OWN RESPONSE SHAPE. `PUT`'s response is `{shares:
// [...]}` — it carries NO `visibility` (only `GET`'s decoded read derives
// that; see forecastShares.js's module header). Rather than invent a SECOND,
// client-side way to compute `visibility` from PUT's shares array — a second
// copy of a derivation that already lives exactly once, server-side — `save`
// re-fetches via `refresh()` after a successful write. This also means what
// the dialog displays after a save is ALWAYS the server's own read of what
// is actually stored, never an optimistic merge of what was just sent.
import { beginIntent } from "../lib/telemetry/instrument";
import { intentArgs } from "../lib/telemetry/uiIntents.js";
import { useCallback, useEffect, useRef, useState } from "react";
import { apiGet, apiPut } from "../lib/apiClient.js";
import { userFacingError } from "../lib/forecast/runErrorText.js";

/**
 * Owner-side share state for ONE forecast: the current lanes/labels, and
 * `save` to whole-set-replace them.
 *
 * @param {object} args
 * @param {string|null|undefined} args.forecastId  the SERVER uuid (same id
 *   `useCopyLinks` takes — never the client_id). Falsy is a supported state
 *   (the dialog may be gated closed, or its forecast id not yet resolved):
 *   no request is made and `loading` settles false.
 * @param {Function} [args.apiGetImpl]  injectable for tests
 * @param {Function} [args.apiPutImpl]  injectable for tests
 * @returns {{
 *   visibility: "private"|"org"|"team",
 *   shares: Array<{lane: "org"|"team"|"user", label: string|null, grantedAt: string}>,
 *   loading: boolean, saving: boolean, error: string|null,
 *   save: (desiredShares: Array<object>) => Promise<boolean>,
 *   refresh: () => Promise<boolean>,
 * }}
 */
export function useForecastShares({ forecastId, apiGetImpl = apiGet, apiPutImpl = apiPut }) {
  const [visibility, setVisibility] = useState("private");
  const [shares, setShares] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // A newly named forecast is loading from its first render, not from the effect that fetches
  // it: a frame painted in between would show the empty form, then the loading line, then the
  // form. Adjusted while rendering (React's pattern for state derived from a changed prop).
  const [loadingFor, setLoadingFor] = useState(forecastId);
  if (forecastId !== loadingFor) {
    setLoadingFor(forecastId);
    setLoading(!!forecastId);
  }

  const seqRef = useRef(0);
  const abortRef = useRef(null);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    if (!forecastId) {
      setVisibility("private");
      setShares([]);
      setLoading(false);
      return false;
    }
    const previous = abortRef.current;
    const ac = new AbortController();
    abortRef.current = ac;
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    if (previous) previous.abort();
    setLoading(true);

    const r = await apiGetImpl(`/api/forecasts/${encodeURIComponent(forecastId)}/shares`, {
      signal: ac.signal,
    });

    if (!aliveRef.current || seq !== seqRef.current) return false;
    setLoading(false);
    if (!r.ok) {
      console.error("[Sayer] share settings load failed:", r.status, r.body);
      setError(
        userFacingError(
          r.body?.error,
          "Couldn't load this forecast's sharing settings. Try again.",
          {
            status: r.status,
          },
        ),
      );
      return false;
    }
    setError(null);
    setVisibility(r.body?.visibility || "private");
    setShares(Array.isArray(r.body?.shares) ? r.body.shares : []);
    return true;
  }, [forecastId, apiGetImpl]);

  useEffect(() => {
    void refresh();
    return () => {
      const ac = abortRef.current;
      if (ac) ac.abort();
    };
  }, [refresh]);

  /**
   * Whole-set replace. `desiredShares` must be the COMPLETE set of shares
   * that should exist after this call — anything absent is revoked
   * (`PUT`'s own contract; forecastShares.js's module header). This hook
   * does not enforce that completeness itself: ShareDialog.jsx owns
   * assembling the full set from what it loaded plus what the user edited.
   *
   * @param {Array<{type: "org", orgId: string} | {type: "team", slug: string} | {type: "user", userId: string}>} desiredShares
   * @returns {Promise<boolean>} true iff the write succeeded
   */
  const save = useCallback(
    async (desiredShares) => {
      if (!forecastId) return false;
      // The user's share (P2 design §4.6): how many people, teams and organisations, never who.
      const count = (type) => desiredShares.filter((d) => d?.type === type).length;
      const intent = beginIntent(
        "share.create",
        { forecast_id: forecastId, org: count("org"), team: count("team"), user: count("user") },
        { origin: "share.save", surface: "share" },
      );
      setSaving(true);
      setError(null);
      let r;
      try {
        r = await apiPutImpl(
          `/api/forecasts/${encodeURIComponent(forecastId)}/shares`,
          { shares: desiredShares },
          ...intentArgs(intent),
        );
      } catch (e) {
        intent.end("error");
        throw e;
      } finally {
        if (aliveRef.current) setSaving(false);
      }
      intent.end(r.ok ? "ok" : "error");
      if (!r.ok) {
        console.error("[Sayer] share settings save failed:", r.status, r.body);
        setError(
          userFacingError(r.body?.error, "Couldn't update who can see this forecast. Try again.", {
            status: r.status,
          }),
        );
        return false;
      }
      // Re-fetch rather than trust PUT's own (visibility-less) response — see
      // module header.
      await refresh();
      return true;
    },
    [forecastId, apiPutImpl, refresh],
  );

  return { visibility, shares, loading, saving, error, save, refresh };
}
